import { FormEvent, useEffect, useId, useRef, useState } from "react";
import * as Popover from "@radix-ui/react-popover";
import {
  CalendarDays,
  ChevronLeft,
  ChevronDown,
  ChevronRight,
  Download,
  Pencil,
  Plus,
  Printer,
  Save,
  X,
} from "lucide-react";
import { usePageHeader } from "contexts/PageHeaderContext";
import { useAuth } from "contexts/AuthContext";
import { useModule } from "contexts/ModuleContext";
import { Button } from "ui/button";
import { Input } from "ui/input";
import { InputFieldset } from "ui/inputFieldset";
import { SelectFieldset } from "ui/selectFieldset";
import { SelectTriggerUI } from "ui/selectTrigger";
import { Label } from "ui/label";
import { Badge } from "ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "ui/card";
import { Checkbox } from "ui/checkbox";
import { Textarea } from "ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "ui/dialog";

type Venue = "Najaf" | "Karbala";
type Meal = "breakfast" | "lunch" | "dinner";
type Menu = Record<Meal, string[]> & { occasion: string };
type Request = { venue: Venue; date: string; items: string; notes: string };

// Complete class strings keep the meal variants discoverable by Tailwind.
const mealStyles: Record<Meal, string> = {
  breakfast: "bg-[#effefc] border-[#a7f0ef] text-[#008780]",
  lunch: "bg-[#fdfaf6] border-[#ffe4ce] text-[#e17c00]",
  dinner: "bg-[#f4f1ff] border-[#d5ccff] text-[#6050ff]",
};

const meals: Meal[] = ["breakfast", "lunch", "dinner"];
const labels: Record<Meal, string> = {
  breakfast: "Breakfast",
  lunch: "Lunch",
  dinner: "Dinner",
};
const emptyMenu = (): Menu => ({
  breakfast: [],
  lunch: [],
  dinner: [],
  occasion: "",
});
const samples: Menu[] = [
  {
    breakfast: ["Masala Chai", "Bun Maska", "Boiled Eggs", "Seasonal Fruits"],
    lunch: ["Mutton Biryani", "Cucumber Mint Raita", "Gulab Jamun"],
    dinner: ["Yellow Dal Tadka", "Jeera Rice", "Phulka", "Paneer Bhaji"],
    occasion: "",
  },
  {
    breakfast: ["Keema Pav", "Sweet Sewiyan", "Butter Toast", "Juice"],
    lunch: ["Dum Pukht Gosht", "Zafrani Pulao", "Mirch Salan", "Firni"],
    dinner: ["Mix Veg Handi", "Tandoori Roti", "Steamed Rice", "Custard"],
    occasion: "",
  },
  {
    breakfast: ["Aloo Paratha with Curd", "Boiled Eggs", "Banana"],
    lunch: ["Mutton Pulao with Dal", "Kachumber Salad"],
    dinner: ["Bhindi Masala", "Chapati", "Moong Dal Khichdi", "Kheer"],
    occasion: "",
  },
  {
    breakfast: ["Club Sandwiches", "Scrambled Eggs", "Hash Browns", "Juice"],
    lunch: ["Special Yakhni Pulao", "Shammi Kebab", "Dalcha", "Phirni"],
    dinner: ["Palak Paneer", "Jeera Rice", "Tawa Roti", "Angoori Jamun"],
    occasion: "Special Occasion",
  },
  {
    breakfast: ["Fried Eggs", "Cheese Omelette", "Croissant", "Black Coffee"],
    lunch: ["Grand Mutton Biryani", "Boondi Raita", "Sirka Pyaz"],
    dinner: [],
    occasion: "",
  },
  {
    breakfast: ["Fried Eggs", "Cheese Omelette", "Croissant", "Black Coffee"],
    lunch: ["Grand Mutton Biryani", "Boondi Raita", "Sirka Pyaz", "Shahi"],
    dinner: ["Dal Makhani", "Butter Naan", "Jeera Rice", "Rabdi"],
    occasion: "",
  },
  {
    breakfast: ["Chole Bhature with Pickled Onions", "Curd", "Tea"],
    lunch: ["Fish Fry", "Steamed Rice", "Goan Curry", "Caramel Pudding"],
    dinner: ["Kadhai Paneer", "Laccha Paratha", "Pulao", "Rasmalai"],
    occasion: "Special Occasion",
  },
  {
    breakfast: ["Pancakes with Honey", "Scrambled Eggs", "Sausage"],
    lunch: ["Mughlai Chicken Biryani", "Mirch Ka Salan", "Malpua"],
    dinner: ["Paneer Tikka Masala", "Butter Kulcha", "Dal Fry", "Ice Cream"],
    occasion: "",
  },
];
const options = Object.fromEntries(
  meals.map((meal) => [
    meal,
    Array.from(new Set(samples.flatMap((menu) => menu[meal]))),
  ]),
) as Record<Meal, string[]>;
const initialMenus: Record<string, Menu> = {};
[0, 1, 2, 3, 4, 0, 5, 6, 7, 0].forEach((sample, index) => {
  initialMenus[`Najaf:2026-09-${String(index + 1).padStart(2, "0")}`] =
    samples[sample];
});
[1, 2, 3].forEach((day, index) => {
  initialMenus[`Karbala:2026-09-0${day}`] = samples[index + 1];
});
const dateKey = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
const parseDate = (date: string) => new Date(`${date}T12:00:00`);
const formatDate = (date: string) =>
  parseDate(date).toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
const hasMeals = (menu?: Menu) =>
  !!menu && meals.some((meal) => menu[meal].length > 0);
const escapeXml = (value: string) =>
  value.replace(
    /[<>&"']/g,
    (character) =>
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      ({
        "<": "&lt;",
        ">": "&gt;",
        "&": "&amp;",
        '"': "&quot;",
        "'": "&apos;",
      })[character]!,
  );

function MealItemPicker({
  meal,
  selected,
  onAdd,
}: {
  meal: Meal;
  selected: string[];
  onAdd: (items: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [pending, setPending] = useState<string[]>([]);
  const searchRef = useRef<HTMLInputElement>(null);
  const id = useId();
  const available = options[meal].filter((item) => !selected.includes(item));
  const matches = available.filter((item) =>
    item.toLowerCase().includes(query.trim().toLowerCase()),
  );
  return (
    <div className="flex min-w-0 items-center gap-2">
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger asChild>
          <Button
            type="button"
            variant="outline"
            className="h-9 min-w-0 flex-1 justify-between bg-white text-sm text-muted-foreground"
            aria-label={`Select ${labels[meal]} items`}
          >
            <span className="truncate">
              {pending.length
                ? `${pending.length} selected`
                : "Add a menu item"}
            </span>
            <ChevronDown />
          </Button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            className="z-50 max-h-[var(--radix-popover-content-available-height)] w-[var(--radix-popover-trigger-width)] overflow-y-auto rounded-lg border border-[#5B9BD5]/30 bg-white p-2 text-foreground shadow-lg"
            sideOffset={4}
            align="start"
            aria-label={`${labels[meal]} menu selection`}
            onOpenAutoFocus={(event) => {
              event.preventDefault();
              searchRef.current?.focus();
            }}
          >
            <Input
              ref={searchRef}
              aria-label={`Search ${labels[meal]} menus`}
              placeholder="Search menus..."
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") event.preventDefault();
              }}
            />
            <div className="mt-2 max-h-48 overflow-y-auto">
              {matches.map((item) => (
                <label
                  key={item}
                  className="flex cursor-pointer items-center gap-2 rounded p-2 text-sm hover:bg-sky-50 focus-within:bg-sky-50"
                  htmlFor={`${id}-${options[meal].indexOf(item)}`}
                >
                  <Checkbox
                    id={`${id}-${options[meal].indexOf(item)}`}
                    checked={pending.includes(item)}
                    onCheckedChange={(checked) =>
                      setPending((current) =>
                        checked === true
                          ? [...current, item]
                          : current.filter((value) => value !== item),
                      )
                    }
                  />
                  <span>{item}</span>
                </label>
              ))}
              {!matches.length && (
                <p className="p-2 text-sm text-muted-foreground">
                  {available.length
                    ? "No menus found."
                    : "All menu items have been added."}
                </p>
              )}
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
      <Button
        type="button"
        className="text-white"
        aria-label={`Add selected ${labels[meal]} items`}
        disabled={!pending.length}
        onClick={() => {
          onAdd(pending);
          setPending([]);
          setQuery("");
          setOpen(false);
        }}
      >
        <Plus />
        Add
      </Button>
    </div>
  );
}

export function MenuCalendarPage() {
  const { setPageHeader } = usePageHeader();
  const { user, permissions } = useAuth();
  const { getScreenIdByPath } = useModule();
  const screenId = getScreenIdByPath("/mawaid");
  const access = screenId === undefined ? undefined : permissions?.[screenId];
  const canAdd = !!user?.isSuperAdmin || !!access?.add;
  const canEdit = !!user?.isSuperAdmin || !!access?.edit;
  const [menus, setMenus] = useState(initialMenus);
  const [requests, setRequests] = useState<Request[]>([]);
  const [from, setFrom] = useState("2026-09-01");
  const [to, setTo] = useState("2026-09-30");
  const [month, setMonth] = useState(new Date(2026, 8, 1));
  const [venue, setVenue] = useState<Venue>("Najaf");
  const [mealView, setMealView] = useState("all");
  const [menuDialog, setMenuDialog] = useState<{
    date: string;
    edit: boolean;
  } | null>(null);
  const [draft, setDraft] = useState<Menu>(emptyMenu);
  const [request, setRequest] = useState<Request | null>(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [requestItemsError, setRequestItemsError] = useState("");
  useEffect(() => {
    setPageHeader({ title: "Menu Calendar", icon: <CalendarDays /> });
    return () => setPageHeader(null);
  }, [setPageHeader]);
  const visibleMeals = meals.filter(
    (meal) => mealView === "all" || meal === mealView,
  );
  const monthLabel = month.toLocaleDateString("en-GB", {
    month: "long",
    year: "numeric",
  });
  const daysInMonth = new Date(
    month.getFullYear(),
    month.getMonth() + 1,
    0,
  ).getDate();
  const cells = Array.from(
    { length: Math.ceil((month.getDay() + daysInMonth) / 7) * 7 },
    (_, index) =>
      new Date(
        month.getFullYear(),
        month.getMonth(),
        index - month.getDay() + 1,
      ),
  );
  const changeMonth = (offset: number) => {
    const next = new Date(month.getFullYear(), month.getMonth() + offset, 1);
    setMonth(next);
    setFrom(dateKey(next));
    setTo(dateKey(new Date(next.getFullYear(), next.getMonth() + 1, 0)));
  };
  const openMenu = (date: string, edit: boolean) => {
    setDraft(edit ? { ...menus[`${venue}:${date}`] } : emptyMenu());
    setError("");
    setMenuDialog({ date, edit });
  };
  const saveMenu = (event: FormEvent) => {
    event.preventDefault();
    if (!menuDialog || !(menuDialog.edit ? canEdit : canAdd)) return;
    if (!hasMeals(draft)) {
      setError("Select at least one menu item.");
      return;
    }
    setMenus((current) => ({
      ...current,
      [`${venue}:${menuDialog.date}`]: {
        ...draft,
        occasion: draft.occasion.trim(),
      },
    }));
    setMenuDialog(null);
  };
  const saveRequest = (event: FormEvent) => {
    event.preventDefault();
    if (!request || !canAdd) return;
    if (!request.items.trim()) {
      setRequestItemsError("Required Items & Volume is required");
      return;
    }

    setRequestItemsError("");
    setRequests((current) => [
      ...current,
      { ...request, items: request.items.trim(), notes: request.notes.trim() },
    ]);
    setNotice(
      `Grocery & supply request saved for ${formatDate(request.date)} at ${request.venue}.`,
    );
    setRequest(null);
  };
  const exportExcel = () => {
    const rows = [
      [
        "Date",
        "Venue",
        ...visibleMeals.map((meal) => labels[meal]),
        "Special Occasion",
      ],
      ...cells
        .filter(
          (day) =>
            day.getMonth() === month.getMonth() &&
            dateKey(day) >= from &&
            dateKey(day) <= to,
        )
        .map((day) => {
          const menu = menus[`${venue}:${dateKey(day)}`];
          return [
            dateKey(day),
            venue,
            ...visibleMeals.map((meal) => menu?.[meal].join(", ") || ""),
            menu?.occasion || "",
          ];
        }),
    ];
    const xml = `<?xml version="1.0" encoding="UTF-8"?><?mso-application progid="Excel.Sheet"?><Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet"><Worksheet ss:Name="Menu Calendar"><Table>${rows.map((row) => `<Row>${row.map((value) => `<Cell><Data ss:Type="String">${escapeXml(value)}</Data></Cell>`).join("")}</Row>`).join("")}</Table></Worksheet></Workbook>`;
    const url = URL.createObjectURL(
      new Blob([xml], { type: "application/vnd.ms-excel;charset=utf-8" }),
    );
    const link = document.createElement("a");
    link.href = url;
    link.download = `menu-calendar-${venue}-${dateKey(month).slice(0, 7)}.xml`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  return (
    <section
      id="mawaid-calendar"
      className="min-w-0 space-y-6 pb-8 text-foreground print:absolute print:left-0 print:top-0 print:w-full print:space-y-0 print:p-0 print:[body:has(&)_*]:invisible print:!visible print:[&_*]:!visible print:[&_*]:[print-color-adjust:exact] print:[&_*]:[-webkit-print-color-adjust:exact]"
      aria-label="Menu Calendar"
    >
      <style>{`@page { size: landscape; margin: 8mm; }`}</style>
      <Card
        className="min-w-0 border-[#5B9BD5]/30 bg-white print:hidden"
        style={{ marginTop: "10px" }}
      >
        <CardContent style={{ padding: "14px" }}>
          <div className="grid min-w-0 grid-cols-1 items-end gap-3 sm:grid-cols-2 xl:grid-cols-[1.1fr_1.1fr_1.1fr_1.2fr_auto]">
            <div className="min-w-0 space-y-2">
              <Label htmlFor="mawaid-from" className="text-xs">
                From
              </Label>
              <Input
                type="date"
                id="mawaid-from"
                aria-label="From Date"
                className="block h-11 min-w-0 rounded-lg px-4 py-2 text-base md:text-base"
                value={from}
                max={to}
                onChange={(event) => {
                  if (!event.target.value) return;
                  setFrom(event.target.value);
                  const next = parseDate(event.target.value);
                  setMonth(new Date(next.getFullYear(), next.getMonth(), 1));
                }}
              />
            </div>
            <div className="min-w-0 space-y-2">
              <Label htmlFor="mawaid-to" className="text-xs">
                To
              </Label>
              <Input
                type="date"
                id="mawaid-to"
                aria-label="To Date"
                className="block h-11 min-w-0 rounded-lg px-4 py-2 text-base md:text-base"
                value={to}
                min={from}
                onChange={(event) => {
                  if (event.target.value) setTo(event.target.value);
                }}
              />
            </div>
            <div className="min-w-0 space-y-2">
              <Label htmlFor="mawaid-meal-view" className="text-xs">
                Meal View
              </Label>

              <Select value={mealView} onValueChange={setMealView}>
                <SelectTrigger
                  id="mawaid-meal-view"
                  aria-label="Meal View"
                  className="block h-11 min-w-0 rounded-lg px-4 py-2 text-sm"
                  style={{ display: "flex" }}
                >
                  <SelectValue />
                </SelectTrigger>

                <SelectContent className="text-sm">
                  <SelectItem value="all">All 3 Meals</SelectItem>

                  {meals.map((meal) => (
                    <SelectItem key={meal} value={meal}>
                      {labels[meal]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-0 space-y-2">
              <span className="block text-xs font-medium leading-none">
                Venue
              </span>
              <div className="flex flex-wrap gap-2">
                {(["Najaf", "Karbala"] as Venue[]).map((value) => (
                  <Button
                    key={value}
                    type="button"
                    aria-pressed={venue === value}
                    variant={venue === value ? "default" : "secondary"}
                    className="h-11 min-w-20 flex-1"
                    onClick={() => setVenue(value)}
                  >
                    {value}
                  </Button>
                ))}
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                className="bg-background"
                onClick={exportExcel}
              >
                <Download />
                Excel
              </Button>
              <Button
                variant="outline"
                className="bg-background"
                onClick={() => window.print()}
              >
                <Printer />
                Print
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
      {from > to && <p role="alert">From Date must be on or before To Date.</p>}
      {notice && (
        <p role="status" className="text-sm text-green-700 print:hidden">
          {notice}
        </p>
      )}
      <Card
        className="min-w-0 overflow-hidden border-[#5B9BD5]/30 bg-white print:!mt-0 print:shadow-none print:backdrop-blur-none"
        style={{ gap: "0px", marginTop: "10px" }}
      >
        <CardHeader
          className="flex flex-row flex-wrap items-center gap-4 border-b bg-gradient-to-r from-sky-50 to-blue-50 px-4 py-3 sm:px-6 print:p-2"
          style={{ display: "flex" }}
        >
          <CardTitle className="text-sm font-semibold text-primary">
            {monthLabel}
          </CardTitle>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8 bg-background print:hidden"
            aria-label="Previous month"
            onClick={() => changeMonth(-1)}
          >
            <ChevronLeft />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8 bg-background print:hidden"
            aria-label="Next month"
            onClick={() => changeMonth(1)}
          >
            <ChevronRight />
          </Button>
          <CardDescription className="hidden print:block print:text-xs">
            {venue} · {from} – {to} ·{" "}
            {mealView === "all" ? "All 3 Meals" : labels[mealView as Meal]}
          </CardDescription>
        </CardHeader>
        <CardContent className="min-w-0" style={{ padding: "0px" }}>
          <div className="max-w-full overflow-x-auto print:overflow-visible">
            <div className="grid min-w-[56rem] grid-cols-7 print:min-w-0">
              {["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"].map((day) => (
                <div
                  key={day}
                  className="bg-secondary px-2 py-4 text-center text-sm font-semibold text-[#0b6b93] print:p-2"
                >
                  {day}
                </div>
              ))}
              {cells.map((day) => {
                const date = dateKey(day);
                const active =
                  day.getMonth() === month.getMonth() &&
                  date >= from &&
                  date <= to;
                const menu = menus[`${venue}:${date}`];
                const populated = hasMeals(menu);
                const count = requests.filter(
                  (item) => item.date === date && item.venue === venue,
                ).length;
                return (
                  <article
                    key={date}
                    aria-label={formatDate(date)}
                    className={`flex min-h-52 min-w-0 flex-col border-b border-r border-border p-2 [&:nth-child(7n)]:border-r-0 print:min-h-32 print:break-inside-avoid${active ? "" : " bg-[#f7fcff] text-[#96a8bf]"}`}
                  >
                    <time className="mb-2 text-sm font-medium" dateTime={date}>
                      {day.getDate()}
                    </time>
                    {active &&
                      (populated ? (
                        <>
                          <div className="flex min-w-0 flex-col gap-2">
                            {visibleMeals.map(
                              (meal) =>
                                menu[meal].length > 0 && (
                                  <div
                                    key={meal}
                                    className={`flex min-w-0 items-baseline gap-1 rounded border p-1 text-xs leading-snug print:text-[8px] ${mealStyles[meal]}`}
                                  >
                                    <strong className="shrink-0">
                                      {labels[meal][0]}:
                                    </strong>
                                    <span className="min-w-0 break-words text-foreground">
                                      {menu[meal].join(", ")}
                                    </span>
                                  </div>
                                ),
                            )}
                          </div>
                          <div className="mt-auto flex min-w-0 flex-wrap items-center justify-between gap-1 pt-4">
                            {menu.occasion && (
                              <Badge
                                variant="outline"
                                className="max-w-full shrink whitespace-normal break-words border-green-300 bg-green-100 px-1.5 py-0.5 text-xs leading-tight text-[#159447] [overflow-wrap:anywhere]"
                                title={menu.occasion}
                              >
                                {menu.occasion}
                              </Badge>
                            )}
                            <div className="ml-auto flex shrink-0 print:hidden">
                              {canAdd && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 p-1 text-primary"
                                  aria-label={`Raise grocery request for ${date}`}
                                  title={
                                    count
                                      ? `${count} saved request(s) — raise another`
                                      : "Raise Grocery & Supply Request"
                                  }
                                  onClick={() => {
                                    setError("");
                                    setRequestItemsError("");
                                    setRequest({
                                      venue,
                                      date,
                                      items: "",
                                      notes: "",
                                    });
                                  }}
                                >
                                  <Plus />
                                </Button>
                              )}
                              {canEdit && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 p-1 text-primary"
                                  aria-label={`Edit meals for ${date}`}
                                  title="Edit meals"
                                  onClick={() => openMenu(date, true)}
                                >
                                  <Pencil />
                                </Button>
                              )}
                            </div>
                          </div>
                        </>
                      ) : canAdd ? (
                        <Button
                          variant="outline"
                          className="mx-2 my-auto h-auto min-h-16 flex-col gap-1 whitespace-normal border-dashed border-primary bg-sky-50 p-3 text-xs font-semibold text-primary hover:bg-sky-100 print:hidden"
                          aria-label={`Add meals for ${date}`}
                          onClick={() => openMenu(date, false)}
                        >
                          <Plus />
                          <span>Add Meals</span>
                        </Button>
                      ) : null)}
                  </article>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>
      <Dialog
        open={!!menuDialog}
        onOpenChange={(open) => {
          if (!open) setMenuDialog(null);
        }}
      >
        <DialogContent className="max-h-[90vh] overflow-y-auto bg-white sm:max-w-xl">
          <form onSubmit={saveMenu}>
            <DialogHeader>
              <DialogTitle className="pr-6 text-primary">
                {menuDialog?.edit ? "Edit" : "Add"} Menu for{" "}
                {menuDialog ? formatDate(menuDialog.date) : ""}
              </DialogTitle>
              <DialogDescription>
                Configure daily meal lineups.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {meals.map((meal) => (
                <section
                  key={meal}
                  className={`min-w-0 rounded-lg border p-4 ${mealStyles[meal]}`}
                >
                  <h3 className="mb-2 text-sm font-medium">{labels[meal]}</h3>
                  {draft[meal].length > 0 && (
                    <div className="mb-2 flex flex-wrap gap-1.5">
                      {draft[meal].map((item) => (
                        <Badge
                          variant="outline"
                          className="max-w-full whitespace-normal break-words border-[#5B9BD5]/30 bg-sky-50 text-foreground"
                          key={item}
                        >
                          {item}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-5 w-5 shrink-0 p-0 text-destructive"
                            type="button"
                            aria-label={`Remove ${item} from ${labels[meal]}`}
                            onClick={() =>
                              setDraft((current) => ({
                                ...current,
                                [meal]: current[meal].filter(
                                  (value) => value !== item,
                                ),
                              }))
                            }
                          >
                            <X className="size-3.5" />
                          </Button>
                        </Badge>
                      ))}
                    </div>
                  )}
                  <MealItemPicker
                    meal={meal}
                    selected={draft[meal]}
                    onAdd={(items) =>
                      setDraft((current) => ({
                        ...current,
                        [meal]: Array.from(
                          new Set([...current[meal], ...items]),
                        ),
                      }))
                    }
                  />
                </section>
              ))}
            </div>
            <div className="pb-4">
              <InputFieldset
                label="Special Occasion Tag"
                aria-label="Special Occasion Tag"
                placeholder="e.g. Milad Special"
                value={draft.occasion}
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    occasion: event.target.value,
                  }))
                }
              />
            </div>
            {error && (
              <p role="alert" className="mb-4 text-sm text-red-700">
                {error}
              </p>
            )}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setMenuDialog(null)}
              >
                Cancel
              </Button>
              <Button type="submit" className="text-white">
                <Save />
                Save
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog
        open={!!request}
        onOpenChange={(open) => {
          if (!open) setRequest(null);
        }}
      >
        <DialogContent className="max-h-[90vh] overflow-y-auto bg-white sm:max-w-xl">
          <form onSubmit={saveRequest}>
            <DialogHeader>
              <DialogTitle className="pr-6 text-primary">
                Raise Grocery &amp; Supply Request
              </DialogTitle>
              <DialogDescription>
                Procurement requisition based on scheduled meal headcount
              </DialogDescription>
            </DialogHeader>
            {request && (
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <SelectFieldset label="Venue">
                    <Select
                      value={request.venue}
                      onValueChange={(value) =>
                        setRequest({ ...request, venue: value as Venue })
                      }
                    >
                      <SelectTriggerUI aria-label="Request Venue">
                        <SelectValue />
                      </SelectTriggerUI>
                      <SelectContent>
                        <SelectItem value="Najaf">Najaf</SelectItem>
                        <SelectItem value="Karbala">Karbala</SelectItem>
                      </SelectContent>
                    </Select>
                  </SelectFieldset>
                  <div className="min-w-0">
                    <InputFieldset
                      label="Date"
                      type="date"
                      aria-label="Request Date"
                      value={request.date}
                      readOnly
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="mawaid-request-items">
                    Required Items &amp; Volume{" "}
                    <span className="text-destructive">*</span>
                  </Label>

                  <Textarea
                    id="mawaid-request-items"
                    aria-label="Required Items & Volume"
                    rows={3}
                    placeholder="e.g. Basmati Rice (150 kg), Cooking Oil (60 L), Takeaway Box sets (250 pcs)..."
                    value={request.items}
                    className={
                      requestItemsError ? "focus-visible:ring-red-500" : ""
                    }
                    onChange={(event) => {
                      setRequest({
                        ...request,
                        items: event.target.value,
                      });

                      if (event.target.value.trim()) {
                        setRequestItemsError("");
                      }
                    }}
                  />

                  {requestItemsError && (
                    <p className="text-red-500 text-xs">{requestItemsError}</p>
                  )}
                </div>
                <div>
                  <InputFieldset
                    label="Additional Notes"
                    aria-label="Additional Notes"
                    placeholder="e.g. High Priority - Needed within 24 hrs"
                    value={request.notes}
                    onChange={(event) =>
                      setRequest({ ...request, notes: event.target.value })
                    }
                  />
                </div>
              </div>
            )}
            {error && (
              <p role="alert" className="mb-4 text-sm text-red-700">
                {error}
              </p>
            )}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setRequest(null)}
              >
                Cancel
              </Button>
              <Button type="submit" className="text-white">
                <Save />
                Save
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
}
